import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import events from '../data/events';

// 2026-2027 school year calendar
const YEARLY_CALENDAR_URL = 'https://docs.google.com/spreadsheets/d/1V3v3BPSEZBYuv855d39n-Yq3x-J5Xlqt/edit?usp=drive_link&ouid=116348819300951825311&rtpof=true&sd=true';
const YEARLY_CALENDAR_XLSX = '/assets/documents/2026_2027_Family_Calendar.xlsx';

const Calendar = () => {
  const [currentView, setCurrentView] = useState('month');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedFilters, setSelectedFilters] = useState({
    holiday: true,
    academic: true,
    earlyDismissal: true,
    testing: true,
    pta: true,
    afterSchool: true
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [language, setLanguage] = useState('en');
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);

  const getDaysInMonth = (month, year) => {
    const date = new Date(year, month, 1);
    const days = [];
    const firstDay = new Date(year, month, 1).getDay();
    for (let i = 0; i < firstDay; i++) days.push(null);
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return days;
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getWeekDays = () => {
    const base = new Date(currentYear, currentMonth, 1);
    const start = getStartOfWeek(base);
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() + currentWeekOffset * 7);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return { weekStart, days };
  };

  const getFilteredEvents = () => {
    return events.filter(event => selectedFilters[event.category]);
  };

  const getUpcomingEvents = () => {
    const today = new Date();
    return getFilteredEvents()
      .filter(event => event.date >= today)
      .sort((a, b) => a.date - b.date)
      .slice(0, 5);
  };

  const prevMonth = () => {
    if (currentView === 'week') { setCurrentWeekOffset(currentWeekOffset - 1); return; }
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); } else { setCurrentMonth(currentMonth - 1); }
  };
  const nextMonth = () => {
    if (currentView === 'week') { setCurrentWeekOffset(currentWeekOffset + 1); return; }
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); } else { setCurrentMonth(currentMonth + 1); }
  };

  const categoryColors = {
    holiday: 'bg-[#630503]',
    academic: 'bg-amber-700',
    earlyDismissal: 'bg-orange-700',
    testing: 'bg-purple-700',
    pta: 'bg-brand-accent-dark',
    afterSchool: 'bg-green-700'
  };

  const getCategoryName = (category) => {
    switch(category) {
      case 'holiday': return 'Holiday';
      case 'academic': return 'Academic';
      case 'earlyDismissal': return 'Early Dismissal';
      case 'testing': return 'Testing';
      case 'pta': return 'PTA';
      case 'afterSchool': return 'After-School';
      default: return category;
    }
  };

  const getCategoryNameES = (category) => {
    switch(category) {
      case 'holiday': return 'Feriado';
      case 'academic': return 'Académico';
      case 'earlyDismissal': return 'Salida Temprana';
      case 'testing': return 'Exámenes';
      case 'pta': return 'PTA';
      case 'afterSchool': return 'Programas Después de Clases';
      default: return category;
    }
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', options);
  };

  const getEventTitle = (event) => (language === 'es' && event.titleES ? event.titleES : event.title);
  const getEventDescription = (event) => (language === 'es' && event.descriptionES ? event.descriptionES : (event.description || ''));

  const toggleFilter = (filter) => setSelectedFilters({ ...selectedFilters, [filter]: !selectedFilters[filter] });

  const getEventsForDay = (day) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === day.getDate() &&
             eventDate.getMonth() === day.getMonth() &&
             eventDate.getFullYear() === day.getFullYear() &&
             (selectedFilters[event.category] || false);
    });
  };

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  const days = getDaysInMonth(currentMonth, currentYear);
  const upcomingEvents = getUpcomingEvents();
  const closeModal = () => setSelectedEvent(null);

  return (
    <>
      <Navbar />
      <main id="main-content" className="bg-white">
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 band-light text-brand-text-primary relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-8 md:mb-16">
              <span className="inline-block bg-white shadow-sm text-brand-text-primary px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm font-medium uppercase tracking-[0.2em] mb-6 md:mb-8 rounded-full">School Events</span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-brand-text-primary mb-6 md:mb-8 leading-[1.1] max-w-4xl mx-auto">School <span className="font-bold">Calendar</span></h1>
              <div className="w-16 md:w-24 h-px bg-brand-accent mx-auto mb-6 md:mb-8"></div>
              <p className="text-base md:text-lg lg:text-xl leading-relaxed text-body max-w-3xl mx-auto px-4">Stay informed about school events, holidays, and important dates throughout the academic year.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 2026-2027 School Calendar */}
      <section className="py-10 md:py-16 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">2026-2027 School Calendar</h2>
            <p className="text-gray-600 mb-6">The full year at a glance — holidays, early dismissals, assessments, and PTA events.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={YEARLY_CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-brand-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-primary/90 transition-colors duration-200 shadow-md"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View on Google Sheets
                <span className="sr-only">(opens in a new tab)</span>
              </a>
              <a
                href={YEARLY_CALENDAR_XLSX}
                className="inline-flex items-center gap-2 bg-white text-brand-primary border-2 border-brand-primary px-6 py-3 rounded-lg font-semibold hover:bg-brand-primary/5 transition-colors duration-200 shadow-md"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 9.293a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Download Calendar (Excel)
              </a>
            </div>
          </div>
          <div className="max-w-6xl mx-auto rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-white">
            <img
              src="/assets/images/calendar/2026_2027_School_Calendar.png"
              alt="School of Authors @ PS/MS 108 2026-2027 school calendar. September 2026: 6th grade orientation, 3K orientation on Zoom September 8, 3K/Pre-K meet the teacher September 9, students return September 10, after-school begins September 14, PTA meeting September 16, Yom Kippur school closed September 21, Back to School Night September 24, PTA outdoor movie night September 25, District 4 Back to School event September 26. The full month-by-month listing through June 2027 is also available on the Monthly Calendar below, or in the Google Sheets and Excel versions linked above."
              className="w-full h-auto"
            />
          </div>
          <p className="text-center text-sm text-gray-500 mt-4">
            Prefer a month-by-month view? Use the interactive calendar below.
          </p>
        </div>
      </section>

      <section id="monthly-calendar" className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20">
            <span className="inline-block bg-brand-primary text-white px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm font-bold uppercase tracking-wider mb-6 md:mb-8 shadow-lg">Interactive Calendar</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-6 md:mb-8 leading-tight">Monthly Calendar</h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">View school events, filter by category, and stay up-to-date with important dates and activities.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="lg:col-span-1 bg-white rounded-xl shadow-lg border border-gray-100 p-6 md:p-8 hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl md:text-2xl font-bold text-brand-primary mb-6 md:mb-8">Filter Events</h3>
              <div className="space-y-3">
                {[
                  ['holiday','Holidays','bg-[#630503]','text-[#630503]'],
                  ['academic','Academic Events','bg-amber-700','text-amber-700'],
                  ['earlyDismissal','Early Dismissal','bg-orange-700','text-orange-700'],
                  ['testing','Testing','bg-purple-700','text-purple-700'],
                  ['pta','PTA Events','bg-brand-accent-dark','text-brand-accent-dark'],
                  ['afterSchool','After-School Programs','bg-green-700','text-green-700'],
                ].map(([key,label,bg,color]) => (
                  <div key={key} className="flex items-center">
                    <input id={`filter-${key}`} type="checkbox" checked={selectedFilters[key]} onChange={() => toggleFilter(key)} className={`h-4 md:h-5 w-4 md:w-5 ${color} rounded border-gray-300 focus:ring-brand-primary focus:ring-offset-2`} />
                    <label htmlFor={`filter-${key}`} className="ml-2 flex items-center text-gray-700 text-sm md:text-base">
                      <span className={`h-3 w-3 ${bg} rounded-full mr-2`}></span>
                      {label}
                    </label>
                  </div>
                ))}
              </div>

              <div className="mt-8 md:mt-10">
                <h3 className="text-lg md:text-xl font-bold text-brand-primary mb-4 md:mb-6">Language</h3>
                <div className="flex space-x-2 mb-4 md:mb-6">
                  <button onClick={() => setLanguage('en')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary ${language === 'en' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} aria-pressed={language === 'en'}>EN</button>
                  <button onClick={() => setLanguage('es')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary ${language === 'es' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} aria-pressed={language === 'es'}>ES</button>
                </div>
              </div>

              <div className="mt-8 md:mt-10">
                <h3 className="text-lg md:text-xl font-bold text-brand-primary mb-4 md:mb-6">View Options</h3>
                <div className="flex flex-wrap gap-2 mb-4 md:mb-6">
                  <button onClick={() => setCurrentView('month')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary ${currentView === 'month' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} aria-pressed={currentView === 'month'}>Month</button>
                  <button onClick={() => { setCurrentView('week'); setCurrentWeekOffset(0); }} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary ${currentView === 'week' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} aria-pressed={currentView === 'week'}>Week</button>
                  <button onClick={() => setCurrentView('list')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary ${currentView === 'list' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} aria-pressed={currentView === 'list'}>List</button>
                </div>
                <button onClick={() => { setCurrentMonth(new Date().getMonth()); setCurrentYear(new Date().getFullYear()); setCurrentWeekOffset(0); }} className="w-full py-3 border border-brand-primary text-brand-primary rounded-md hover:bg-brand-primary hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 font-semibold">Today</button>
              </div>

              <div className="mt-8 md:mt-10">
                <h3 className="text-lg md:text-xl font-bold text-[#630503] mb-4 md:mb-6">Upcoming Events</h3>
                <div className="space-y-4">
                  {upcomingEvents.length > 0 ? (
                    upcomingEvents.map((event) => (
                      <div key={event.id} className="border-l-4 pl-4 py-2" style={{
                        borderColor: event.category === 'holiday' ? '#630503' : event.category === 'academic' ? '#d97706' : event.category === 'earlyDismissal' ? '#f97316' : event.category === 'testing' ? '#9333ea' : event.category === 'pta' ? '#eab308' : '#16a34a'
                      }}>
                        <div className="text-sm text-gray-600">{formatDate(event.date)}</div>
                        <div className="font-medium text-gray-900">{getEventTitle(event)}</div>
                        <div className="text-sm text-gray-700">{event.allDay ? 'All Day' : `${event.startTime || ''}${event.startTime && event.endTime ? ' - ' : ''}${event.endTime || ''}`}</div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 italic">No upcoming events based on your filters.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="bg-brand-primary text-white px-4 md:px-8 py-4 md:py-6 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    {currentView === 'week' ? (
                      (() => {
                        const { days } = getWeekDays();
                        const startLabel = `${days[0].toLocaleString('default', { month: 'short' })} ${days[0].getDate()}`;
                        const endLabel = `${days[6].toLocaleString('default', { month: 'short' })} ${days[6].getDate()}, ${days[6].getFullYear()}`;
                        return <h3 className="text-base md:text-xl font-bold truncate">Week of {startLabel} – {endLabel}</h3>;
                      })()
                    ) : (
                      <h3 className="text-lg md:text-xl font-bold">{monthNames[currentMonth]} {currentYear}</h3>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={prevMonth} className="p-2 rounded-full hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-primary" aria-label="Previous month">
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    </button>
                    <button onClick={nextMonth} className="p-2 rounded-full hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-primary" aria-label="Next month">
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 text-center bg-gray-50 border-b border-gray-200">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day, i) => (
                    <div key={i} className="py-2 font-medium text-gray-700 text-xs md:text-base">{day}</div>
                  ))}
                </div>

                {currentView === 'month' && (
                  <div className="grid grid-cols-7 auto-rows-fr border-b border-gray-200 min-h-[400px] md:min-h-[600px]">
                    {days.map((day, index) => {
                      const dayEvents = day ? getEventsForDay(day) : [];
                      const isToday = day && day.toDateString() === new Date().toDateString();
                      return (
                        <div key={index} className={`border-t border-l p-1 md:p-2 h-24 md:h-32 ${day ? 'bg-white' : 'bg-gray-50 text-gray-400'} ${isToday ? 'bg-[#630503]/5 ring-2 ring-inset ring-[#630503]' : ''} ${index % 7 === 6 ? 'border-r' : ''}`}>
                          {day && (
                            <>
                              <div className={`text-right mb-1 text-xs md:text-base ${isToday ? 'font-bold text-[#630503]' : ''}`}>{day.getDate()}</div>
                              <div className="space-y-1 overflow-y-auto max-h-[60px] md:max-h-[80px]">
                                {dayEvents.map((event) => (
                                  <button key={event.id} onClick={() => setSelectedEvent(event)} className={`text-[10px] md:text-xs w-full text-left px-1 md:px-2 py-0.5 md:py-1 rounded text-white ${categoryColors[event.category]} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] flex items-center justify-between`} title={getEventTitle(event)} aria-label={`${getEventTitle(event)} on ${formatDate(event.date)}`}>
                                    <span className="truncate">{getEventTitle(event)}</span>
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {currentView === 'week' && (() => {
                  const { days: weekDays } = getWeekDays();
                  return (
                    <div className="border-b border-gray-200 min-h-[400px]">
                      <div className="grid grid-cols-7 text-center bg-gray-50 border-b border-gray-200">
                        {weekDays.map((d, i) => (
                          <div key={i} className="py-2 font-medium text-gray-700">
                            <div>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]}</div>
                            <div className="text-xs text-gray-500">{d.toLocaleString('default', { month: 'short' })} {d.getDate()}</div>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 auto-rows-fr min-h-[320px]">
                        {weekDays.map((day, index) => {
                          const dayEvents = getEventsForDay(day);
                          const isToday = day.toDateString() === new Date().toDateString();
                          return (
                            <div key={index} className={`border-t border-l p-3 ${index % 7 === 6 ? 'border-r' : ''} ${isToday ? 'bg-[#630503]/5 ring-2 ring-inset ring-[#630503]' : 'bg-white'}`}>
                              <div className={`text-right mb-2 ${isToday ? 'font-bold text-[#630503]' : ''}`}>{day.getDate()}</div>
                              <div className="space-y-2 overflow-y-auto max-h-[260px]">
                                {dayEvents.length === 0 && (<div className="text-xs text-gray-400 italic">{language === 'es' ? 'Sin eventos' : 'No events'}</div>)}
                                {dayEvents.map((event) => (
                                  <button key={event.id} onClick={() => setSelectedEvent(event)} className={`text-xs w-full text-left px-2 py-1 rounded text-white ${categoryColors[event.category]} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] flex items-center justify-between`} title={getEventTitle(event)} aria-label={`${getEventTitle(event)} on ${formatDate(event.date)}`}>
                                    <span className="truncate">{getEventTitle(event)}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {currentView === 'list' && (
                  <div className="border-b border-gray-200 min-h-[600px] p-4">
                    <div className="space-y-4">
                      {getFilteredEvents()
                        .filter(event => event.date.getMonth() === currentMonth && event.date.getFullYear() === currentYear)
                        .sort((a, b) => a.date - b.date)
                        .map(event => (
                          <div key={event.id} className="flex border-l-4 p-4 bg-gray-50 rounded-r" style={{
                            borderColor: event.category === 'holiday' ? '#630503' : event.category === 'academic' ? '#d97706' : event.category === 'earlyDismissal' ? '#f97316' : event.category === 'testing' ? '#9333ea' : event.category === 'pta' ? '#eab308' : '#16a34a'
                          }}>
                            <div className="mr-6 text-center">
                              <div className="font-bold text-lg">{event.date.getDate()}</div>
                              <div className="text-xs text-gray-600">{event.date.toLocaleString('default', { month: 'short' })}</div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{getEventTitle(event)}</div>
                              <div className="text-sm text-gray-700">{event.allDay ? 'All Day' : `${event.startTime || ''}${event.startTime && event.endTime ? ' - ' : ''}${event.endTime || ''}`}</div>
                              <div className="mt-1 text-sm text-gray-600">{getEventDescription(event)}</div>
                              <div className="mt-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  event.category === 'holiday' ? 'bg-[#630503]/10 text-[#630503]' : event.category === 'academic' ? 'bg-amber-100 text-amber-900' : event.category === 'earlyDismissal' ? 'bg-orange-100 text-orange-900' : event.category === 'testing' ? 'bg-purple-100 text-purple-900' : event.category === 'pta' ? 'bg-yellow-100 text-yellow-900' : 'bg-green-100 text-green-900'
                                }`}>
                                  {language === 'es' ? getCategoryNameES(event.category) : getCategoryName(event.category)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Footer removed per request */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {selectedEvent && (
        <div className="fixed inset-0 z-10 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={closeModal}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className={`${categoryColors[selectedEvent.category]} py-6 px-8`}>
                <h3 className="text-2xl font-bold text-white">{getEventTitle(selectedEvent)}</h3>
                <p className="text-white/90 mt-2 text-lg">{formatDate(selectedEvent.date)}</p>
              </div>
              <div className="bg-white px-8 py-6">
                <div className="text-gray-600">
                  <p className="mb-2 text-lg leading-relaxed">{getEventDescription(selectedEvent)}</p>
                  {(selectedEvent.description || selectedEvent.descriptionES) && (
                    <p className="mb-6 text-sm leading-relaxed text-gray-500 italic">{language === 'es' ? selectedEvent.description : (selectedEvent.descriptionES || '')}</p>
                  )}
                  {(selectedEvent.time || selectedEvent.startTime || selectedEvent.endTime || selectedEvent.allDay) && (
                    <div className="flex items-center mb-3">
                      <svg className="h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3a.75.75 0 011.5 0v1.5h.75A2.25 2.25 0 0121 6.75v10.5A2.25 2.25 0 0118.75 19.5H5.25A2.25 2.25 0 013 17.25V6.75A2.25 2.25 0 015.25 4.5H6V3a.75.75 0 01.75-.75zM4.5 9h15v8.25a.75.75 0 01-.75.75H5.25a.75.75 0 01-.75-.75V9z" clipRule="evenodd" /></svg>
                      <span>{selectedEvent.allDay ? (language === 'es' ? 'Todo el día' : 'All Day') : (selectedEvent.time ? selectedEvent.time : `${selectedEvent.startTime || ''}${selectedEvent.startTime && selectedEvent.endTime ? ' - ' : ''}${selectedEvent.endTime || ''}`)}</span>
                    </div>
                  )}
                  {selectedEvent.location && (
                    <div className="flex items-center mb-3">
                      <svg className="h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" /></svg>
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}
                  {selectedEvent.flyer && (
                    selectedEvent.flyer.endsWith('.pdf') ? (
                      <a href={selectedEvent.flyer} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-[#630503] hover:underline mt-2 font-medium">
                        <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" /></svg>
                        {language === 'es' ? 'Ver Volante (PDF)' : 'View Flyer (PDF)'}
                      </a>
                    ) : (
                      <div className="mt-4">
                        <img src={selectedEvent.flyer} alt={`${getEventTitle(selectedEvent)} flyer`} className="w-full rounded-lg shadow-md" />
                      </div>
                    )
                  )}
                </div>
              </div>
              <div className="bg-gray-50 px-8 py-4 flex justify-end space-x-4">
                <button type="button" className="px-6 py-3 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] transition-colors" onClick={closeModal}>Close</button>
                {selectedEvent.link && (
                  <a href={selectedEvent.link} className="inline-flex justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-[#630503] hover:bg-[#4a0402] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] transition-colors" target="_blank" rel="noopener noreferrer" aria-label={`More details about ${getEventTitle(selectedEvent)}`}>More Details</a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      </main>
      <Footer />
    </>
  );
};

export default Calendar;


